import { useState } from "react";
import {
  Box, SimpleGrid, Image, Text, Heading, Wrap, WrapItem, Tag, Button, Link, HStack, IconButton, Flex,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import { useProjects, useCategories, pick } from "../hooks/useContent";
import { useLanguage } from "../context/LanguageContext";
import { projectCategories } from "../admin/collectionsConfig";
import { GridCardsSkeleton } from "./ui/Skeletons";
import Section from "./ui/Section";

// Normalize a project's images into an array (new `images` field, or legacy image_url).
function projectImages(p) {
  if (Array.isArray(p.images) && p.images.length) return p.images;
  if (p.image_url) return [p.image_url];
  return [];
}

// Resolve a link's label for the current language. Supports bilingual links
// ({label_es,label_en}) and legacy single-label links; generic/empty labels
// fall back to "Enlace"/"Link".
function linkLabel(link, language) {
  const raw =
    (language === "es" ? link.label_es : link.label_en) || link.label || "";
  const generic = ["enlace", "link", "ver proyecto", "view project", ""];
  if (!raw || generic.includes(raw.trim().toLowerCase())) {
    return language === "es" ? "Enlace" : "Link";
  }
  return raw;
}

function Carousel({ images, alt }) {
  const [i, setI] = useState(0);
  if (!images.length) return <Box bg="gray.700" height="200px" />;
  const prev = () => setI((n) => (n - 1 + images.length) % images.length);
  const next = () => setI((n) => (n + 1) % images.length);
  return (
    <Box position="relative" height="200px" bg="gray.900">
      <Image src={images[i]} alt={alt} width="100%" height="200px" objectFit="cover" />
      {images.length > 1 && (
        <>
          <IconButton
            aria-label="prev" icon={<FaChevronLeft />} size="sm" position="absolute"
            top="50%" left={2} transform="translateY(-50%)" onClick={prev} borderRadius="full" opacity={0.85}
          />
          <IconButton
            aria-label="next" icon={<FaChevronRight />} size="sm" position="absolute"
            top="50%" right={2} transform="translateY(-50%)" onClick={next} borderRadius="full" opacity={0.85}
          />
          <HStack position="absolute" bottom={2} left="50%" transform="translateX(-50%)" spacing={1}>
            {images.map((_, idx) => (
              <Box key={idx} boxSize={2} borderRadius="full" bg={idx === i ? "brand.400" : "whiteAlpha.600"} />
            ))}
          </HStack>
        </>
      )}
    </Box>
  );
}

function Projects() {
  const { data: projects = [], loading } = useProjects();
  const { data: dbCats } = useCategories("projects");
  const { language } = useLanguage();
  const [filter, setFilter] = useState("all");

  const catLabel = (c) => (language === "es" ? c.label_es : c.label_en);
  const categories = dbCats.length ? dbCats : projectCategories;
  const presentCategories = categories.filter((c) =>
    projects.some((p) => p.category === c.value)
  );
  const filtered =
    filter === "all" ? projects : projects.filter((p) => p.category === filter);

  const eyebrow = language === "es" ? "PORTAFOLIO" : "PORTFOLIO";
  const title = language === "es" ? "Proyectos" : "Projects";

  if (loading) {
    return (
      <Section id="projects" eyebrow={eyebrow} title={title}>
        <GridCardsSkeleton count={6} />
      </Section>
    );
  }

  return (
    <Section id="projects" eyebrow={eyebrow} title={title}>
      {presentCategories.length > 0 && (
        <Wrap spacing={2} mb={6}>
          <WrapItem>
            <Button
              size="sm"
              variant={filter === "all" ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => setFilter("all")}
            >
              {language === "es" ? "Todos" : "All"}
            </Button>
          </WrapItem>
          {presentCategories.map((c) => (
            <WrapItem key={c.value}>
              <Button
                size="sm"
                variant={filter === c.value ? "solid" : "outline"}
                colorScheme="blue"
                onClick={() => setFilter(c.value)}
              >
                {catLabel(c)}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filtered.map((p) => {
          const links =
            Array.isArray(p.links) && p.links.length
              ? p.links
              : p.url
              ? [{ url: p.url }]
              : [];
          return (
            <Box
              key={p.id}
              bg="bg.card"
              border="1px solid"
              borderColor="border.card"
              rounded="xl"
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <Carousel images={projectImages(p)} alt={pick("title", p, language)} />
              <Box p={6} flex={1} display="flex" flexDirection="column">
                <Heading size="sm" fontWeight="bold" mb={2} color="text.heading">
                  {pick("title", p, language)}
                </Heading>
                <Text fontSize="sm" color="text.muted" mb={4} flex={1}>
                  {pick("description", p, language)}
                </Text>
                {p.tags && p.tags.length > 0 && (
                  <Wrap mb={4} spacing={2}>
                    {p.tags.map((tag) => (
                      <WrapItem key={tag}>
                        <Tag size="sm" colorScheme="blue" variant="subtle">
                          {tag}
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
                {links.length > 0 && (
                  <Flex gap={2} wrap="wrap">
                    {links.map((l, idx) => (
                      <Link key={idx} href={l.url} isExternal rel="noopener noreferrer" _hover={{ textDecoration: "none" }}>
                        <Button size="sm" colorScheme="blue" variant="outline" rightIcon={<FaExternalLinkAlt />}>
                          {linkLabel(l, language)}
                        </Button>
                      </Link>
                    ))}
                  </Flex>
                )}
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>
    </Section>
  );
}

export default Projects;
